import React from 'react'

interface FeatureCardProps {
  emoji: string
  title: string
  description: string
  colorFrom?: string
  colorTo?: string
  borderColor?: string
}

export function FeatureCard({ 
  emoji, 
  title, 
  description, 
  colorFrom = 'from-purple-100',
  colorTo = 'to-pink-50',
  borderColor = 'border-purple-200'
}: FeatureCardProps): React.ReactElement {
  return (
    <div className={`p-8 bg-gradient-to-br ${colorFrom} ${colorTo} rounded-3xl border-4 ${borderColor} shadow-lg hover:shadow-xl transition transform hover:-translate-y-2`}>
      <div className="text-6xl mb-3">{emoji}</div>
      <h3 className="text-2xl font-black text-purple-700 mb-1">{title}</h3>
      <p className="text-gray-700 font-semibold">{description}</p>
    </div>
  )
}

interface StatCardProps {
  number?: string
  value?: string | number
  label?: string
  title?: string
  subtitle?: string
  change?: number
  trend?: string
  icon?: string
  colorFrom?: string
  colorTo?: string
  borderColor?: string
  textColor?: string
  className?: string
}

export function StatCard({ 
  number,
  value,
  label,
  title,
  subtitle, 
  change,
  trend,
  icon,
  colorFrom = 'from-purple-100',
  colorTo = 'to-pink-50',
  borderColor = 'border-purple-200',
  textColor = 'text-purple-700',
  className = ''
}: StatCardProps): React.ReactElement {
  const displayNumber = number || value || '0'
  const displayTitle = title || label || 'Stat'
  
  return (
    <div className={`text-center p-10 bg-gradient-to-br ${colorFrom} ${colorTo} rounded-3xl border-4 ${borderColor} shadow-lg ${className}`}>
      {icon && <div className="text-4xl mb-2">{icon}</div>}
      <div className={`text-7xl font-black ${textColor} mb-3`}>{displayNumber}</div>
      <p className={`text-2xl font-black ${textColor}`}>{displayTitle}</p>
      {subtitle && <p className={`${textColor.replace('700', '600')} font-bold`}>{subtitle}</p>}
      {trend && <p className={`text-sm font-bold ${trend === 'up' ? 'text-green-600' : 'text-red-600'}`}>{trend}</p>}
    </div>
  )
}

interface GameCardProps {
  title: string
  description?: string
  children?: React.ReactNode
  icon?: string
  onClick?: () => void
  variant?: 'default' | 'small' | 'large'
  badge?: string
  badges?: string[]
  className?: string
}

export function GameCard({ 
  title, 
  description, 
  children,
  icon, 
  onClick,
  variant = 'default',
  badge,
  badges = [],
  className = ''
}: GameCardProps): React.ReactElement {
  const sizeClasses = {
    small: 'p-4',
    default: 'p-6',
    large: 'p-8'
  }

  const allBadges = badge ? [badge, ...badges] : badges

  return (
    <div 
      onClick={onClick}
      className={`${sizeClasses[variant]} bg-gradient-to-br from-purple-100 to-pink-50 rounded-2xl border-3 border-purple-200 shadow-lg hover:shadow-xl transition transform hover:-translate-y-1 cursor-pointer relative ${className}`}
    >
      {allBadges.length > 0 && (
        <div className="absolute top-2 right-2 flex gap-1">
          {allBadges.map((b, idx) => (
            <div key={idx} className="bg-pink-400 text-white px-3 py-1 rounded-full text-xs font-black">
              {b}
            </div>
          ))}
        </div>
      )}
      {icon && <div className="text-4xl mb-2">{icon}</div>}
      <h3 className="text-lg font-black text-purple-700 mb-1">{title}</h3>
      {description && <p className="text-gray-700 font-semibold text-sm">{description}</p>}
      {children && <div>{children}</div>}
    </div>
  )
}

interface SectionProps {
  title?: string
  subtitle?: string
  children: React.ReactNode
  backgroundColor?: string
  backgroundGradient?: string
}

export function Section({ 
  title, 
  subtitle, 
  children, 
  backgroundColor = 'white',
  backgroundGradient
}: SectionProps): React.ReactElement {
  const bgClass = backgroundGradient || `bg-${backgroundColor}`

  return (
    <section className={`px-8 py-20 ${bgClass}`}>
      <div className="max-w-7xl mx-auto">
        {title && (
          <div className="text-center mb-16">
            <h2 className="text-5xl font-black text-purple-700 mb-2">
              {title}
            </h2>
            {subtitle && (
              <p className="text-xl text-gray-600 font-bold">
                {subtitle}
              </p>
            )}
          </div>
        )}
        {children}
      </div>
    </section>
  )
}

interface ButtonProps {
  children: React.ReactNode
  onClick?: () => void
  variant?: 'primary' | 'secondary' | 'outline'
  size?: 'sm' | 'md' | 'lg'
  className?: string
  disabled?: boolean
}

export function Button({ 
  children, 
  onClick, 
  variant = 'primary',
  size = 'md',
  className = '',
  disabled = false
}: ButtonProps): React.ReactElement {
  const baseClass = 'font-black rounded-full shadow-lg hover:shadow-xl transition transform disabled:opacity-50 disabled:cursor-not-allowed'
  
  const sizeClasses = {
    sm: 'px-4 py-2 text-sm',
    md: 'px-8 py-3 text-lg',
    lg: 'px-12 py-5 text-2xl'
  }

  const variantClasses = {
    primary: 'bg-gradient-to-r from-pink-400 to-purple-400 text-white hover:scale-105',
    secondary: 'bg-gradient-to-r from-blue-400 to-purple-400 text-white hover:scale-105',
    outline: 'border-4 border-purple-300 text-purple-700 hover:bg-purple-50'
  }

  return (
    <button 
      onClick={onClick}
      disabled={disabled}
      className={`${baseClass} ${sizeClasses[size]} ${variantClasses[variant]} ${className}`}
    >
      {children}
    </button>
  )
}
